import { useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Alert, Grid2, Paper, Stack, Typography } from '@mui/material';
import { FormField, FormFieldType } from '@enroller/admissions-components';

import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext';
import { usePageTitle } from '@enroller/utils';
import { yesNoOptions } from '../../../constants/yesNoOptions';
import { defaultValues, FamilyDetailsFormValues, familyDetailsSchema } from './FamilyDetailsFormValues';
import { zodResolver } from '@hookform/resolvers/zod';

const MAX_CHARACTERS_DESCRIPTION = 1000;

const religiousServiceFrequencyOptions = [
  { label: 'Never', value: 'Never' },
  { label: 'A few times a year', value: 'A few times a year' },
  { label: 'Weekly', value: 'Weekly' },
  { label: 'More than once a week', value: 'More than one per week' },
];

const studentAttendOptions = [
  { label: 'If they wish to', value: 'If the student wishes to' },
  { label: 'Yes', value: 'Yes' },
  { label: 'No preference', value: 'No preference' },
];

const religionOptions = [
  { label: 'Unaffiliated', value: 'Unaffiliated' },
  { label: 'Protestant (non-evangelical)', value: 'Protestant (non-evangelical)' },
  { label: 'Protestant (evangelical)', value: 'Protestant (evangelical)' },
  { label: 'Catholic', value: 'Catholic' },
  { label: 'Latter-day Saint (Mormon)', value: 'Latter-day Saint (Mormon)' },
  { label: 'Orthodox Christian', value: 'Orthodox Christian' },
  { label: 'Jewish', value: 'Jewish' },
  { label: 'Muslim', value: 'Muslim' },
  { label: 'Buddhist', value: 'Buddhist' },
  { label: 'Hindu', value: 'Hindu' },
  { label: 'Unitarian/Universalist', value: 'Unitarian/Universalist' },
  { label: "Jehovah's Witness", value: "Jehovah's Witness" },
  { label: 'Other', value: 'Other' },
];

const smokeOptions = [
  { label: 'Yes (Indoors, Outdoors, Car, etc.)', value: 'Yes (indoor, outdoors, car)' },
  { label: 'Yes (Only outdoors)', value: 'Yes (outdoors only)' },
  { label: 'No', value: 'no' },
];

const drinkFrequencyOptions = [
  { label: 'No', value: 'no' },
  { label: 'Seldom', value: 'Seldom' },
  { label: 'Sometimes', value: 'Sometimes' },
  { label: 'Regularly', value: 'Regularly' },
];

const incomeRangeOptions = [
  { label: 'Less than $30,000', value: 'Less than $30,000' },
  { label: '$30,001 - $40,000', value: '$30,001-$40,000' },
  { label: '$40,001 - $50,000', value: '$40,001-$50,000' },
  { label: '$50,001 - $60,000', value: '$50,001-$60,000' },
  { label: '$60,001 - $70,000', value: '$60,001-$70,000' },
  { label: '$70,001 - $80,000', value: '$70,001-$80,000' },
  { label: 'Above $80,000', value: 'Above $80,000' },
];

interface FamilyDetailsFormProps {
  applicationData?: FieldValues;
  submitApplication: (data: FieldValues) => void;
}

export const FamilyDetailsForm = ({ applicationData, submitApplication }: FamilyDetailsFormProps) => {
  const { setSubmit, setHeaderTitle, setIsFormLoading } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');

  useEffect(() => {
    setHeaderTitle({ title: 'Family Details', subtitle: '' });
    setPageTitle('Family Details');
  }, [setHeaderTitle, setPageTitle]);

  // Define resolver based on validation mode needed
  const resolver = useMemo(
    () => zodResolver(validationMode === ValidationType.Full ? familyDetailsSchema.required : familyDetailsSchema.optional),
    [validationMode],
  );

  const { control, handleSubmit, reset, watch } = useForm<FamilyDetailsFormValues>({ defaultValues, resolver });

  useEffect(() => {
    // TODO: map yesno values in StudentApplication.ts
    if (applicationData) {
      reset({
        ...defaultValues,
        ...applicationData,
      });
    }
  }, [applicationData, reset]);

  const onSubmit = useCallback(
    () =>
      handleSubmit((data) => {
        // Format data
        console.log('Form submitted: ', data);
        setIsFormLoading(true);
        submitApplication(data);

        setIsFormLoading(false);
      })(),
    [handleSubmit, setIsFormLoading, submitApplication],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  const watchReligiousAffiliation = watch('religion.religiousAffiliation.value');
  const watchDietaryRestrictions = watch('diet.dietaryRestrictions.value');
  const watchChronicIllness = watch('miscLifestyle.chronicIllness.value');
  const watchMentalDisorder = watch('miscLifestyle.mentalDisorder.value');
  const watchConvicted = watch('miscLifestyle.convicted.value');
  const watchContactWithChildServices = watch('miscLifestyle.contactWithChildServices.value');
  const watchPublicAssistance = watch('financial.publicAssistance.value');

  return (
    <Stack component="form" onSubmit={onSubmit} gap={2}>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={3}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Family Details</Typography>
          </Grid2>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Household Members</Typography>
          </Grid2>
          <Grid2 size={12}>
            <Alert variant="outlined" severity="info">
              <Typography variant="body2">Please add all adults living in the home including yourself.</Typography>
            </Alert>
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="Describe your family, its individual members and how they relate to each other:"
              name="householdMembers.familyDescription"
              control={control}
              multiline
              rows={6}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="Describe any special circumstances (e.g, parents who travel often or work unusual hours, Illness of grandparents, etc.) to which the exchange student wil need to adapt. If none, please type 'none' in the field:"
              name="householdMembers.familySpecialCircumstances"
              control={control}
              multiline
              rows={6}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
        </Grid2>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={4}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">A Typical Day</Typography>
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="Describe a typical weekday in your home:"
              name="typicalDay.typicalWeekday"
              control={control}
              multiline
              rows={6}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="Describe a typical weekend in your home:"
              name="typicalDay.typicalWeekend"
              control={control}
              multiline
              rows={6}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="When you do something special on a weekend, what might you do?"
              name="typicalDay.specialWeekend"
              control={control}
              multiline
              rows={6}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
        </Grid2>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Religion</Typography>
          </Grid2>
          <Grid2 size={12}>
            <Alert variant="outlined" severity="info">
              A host family may want the exchange visitor to attend one or more religious services or programs with the family. The exchange visitor cannot be
              required to do so, but may decide to experience this facet of U.S. culture at his or her discretion.
            </Alert>
          </Grid2>
          <Grid2 size={6}>
            <FormField
              type={FormFieldType.Dropdown}
              label="What is your family's religious affiliation (if any?)"
              name="religion.religiousAffiliation.value"
              control={control}
              options={religionOptions}
            />
          </Grid2>
          <Grid2 size={6}>
            <FormField
              type={FormFieldType.Text}
              label="If Other, please explain"
              name="religion.religiousAffiliation.other"
              control={control}
              disabled={watchReligiousAffiliation !== 'Other'}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Radio}
              label="How often do you attend religious services as a family?"
              name="religion.religiousServiceFrequency"
              control={control}
              options={religiousServiceFrequencyOptions}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Radio}
              label="Would you prefer that your exchange student join you:"
              name="religion.studentAttendPreference"
              control={control}
              options={studentAttendOptions}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Radio}
              label="Would any member of the household have difficulty hosting a student whose religious beliefs were different from their own?"
              name="religion.difficultIfNotSameReligion"
              control={control}
              options={yesNoOptions}
            />
          </Grid2>
        </Grid2>
      </Paper>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Dietary Restrictions</Typography>
          </Grid2>
          <Grid2 size={12}>
            <FormField
              control={control}
              name="diet.provideStudentMealsAgree"
              type={FormFieldType.Checkbox}
              label="We agree that we will provide the exchange student with three square meals a day   (Please check the box if you agree to this statement)"
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              control={control}
              name="diet.dietaryRestrictions.value"
              type={FormFieldType.Radio}
              options={yesNoOptions}
              label="Does your family follow any dietary restrictions due to food allergies, religious or self-imposed beliefs (e.g. vegetarianism)?"
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="If yes, please explain:"
              name="diet.dietaryRestrictions.explanation"
              control={control}
              multiline
              rows={6}
              disabled={watchDietaryRestrictions !== 'yes'}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
          <Grid2 size={6}>
            <FormField
              control={control}
              name="diet.studentFollowRestrictions"
              type={FormFieldType.Radio}
              options={yesNoOptions}
              label="Would you expect the student to follow any dietary restrictions?"
            />
          </Grid2>
          <Grid2 size={6}>
            <FormField
              control={control}
              name="diet.comfortableStudentRestrictions"
              type={FormFieldType.Radio}
              options={yesNoOptions}
              label="Would you feel comfortable hosting a student who follows a particular dietary restriction?"
            />
          </Grid2>
        </Grid2>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Misc / Lifestyle</Typography>
          </Grid2>
          <Grid2 size={6}>
            <FormField
              control={control}
              name="miscLifestyle.carInsurance"
              type={FormFieldType.Radio}
              options={[...yesNoOptions, { label: 'We do not have a vehicle', value: 'We do not have a vehicle' }]}
              label="Do you have auto insurance for all the vehicles used by your household?"
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              control={control}
              name="miscLifestyle.firearms"
              type={FormFieldType.Radio}
              options={yesNoOptions}
              label="Are there any firearms in the home?"
            />
          </Grid2>
          <Grid2 size={12}>
            <Alert variant="outlined" severity="info">
              <Typography variant="body2">
                We, a Host Family for Greenheart Exchange High School Program, hereby agree* to follow all applicable laws regarding the safe and responsible
                use of firearms. We understand that we (a) are required to be knowledgeable about any applicable local or state law concerning our obligation to
                deny children access to firearms and laws regarding gun ownership, storage, transport, etc.; (b) will ensure that all firearms are kept securely
                locked at all times; (c) are required to instruct our children (regardless of age) that even if they have permission to access a firearm in our
                home, they will not allow, encourage or provide access to the firearm to the exchange student; (d) will not allow our children or other minors
                under the age of 18 access to the key to the receptacle/area in which the firearm(s) is stored; and (e) will, under no circumstance, allow our
                exchange student have access to or contact with any firearms regardless of the purpose (including for hunting, target shooting, or other
                recreational use). We further understand that our failure to adhere to these rules may result in legal liability against us collectively or
                individually.
                <br />
                <br />
                *Agreement established by signature of all family members and any other individuals 18 years of age or older residing in the host family home
                during exchange student&apos;s stay.
              </Typography>
            </Alert>
          </Grid2>
          <Grid2 size={12}>
            <FormField
              control={control}
              name="miscLifestyle.firearmLawAgree"
              type={FormFieldType.Checkbox}
              label="By checking the box, I am agreeing to the conditions above."
            />
          </Grid2>
          <Grid2 size={6}>
            <FormField
              control={control}
              name="miscLifestyle.familySmoke"
              type={FormFieldType.Radio}
              options={smokeOptions}
              label="Does anyone in your family smoke?"
            />
          </Grid2>
          <Grid2 size={6}>
            <FormField
              control={control}
              name="miscLifestyle.familyDrink"
              type={FormFieldType.Radio}
              options={drinkFrequencyOptions}
              label="Does anyone in your family drink alcoholic beverages?"
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              control={control}
              name="miscLifestyle.chronicIllness.value"
              type={FormFieldType.Radio}
              options={yesNoOptions}
              label="Does anyone in your family have a serious or chronic illness?"
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="If yes, please explain. Provide a brief summary of what the illness is and how it may affect your family's daily life."
              name="miscLifestyle.chronicIllness.explanation"
              control={control}
              multiline
              rows={6}
              disabled={watchChronicIllness !== 'yes'}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              control={control}
              name="miscLifestyle.mentalDisorder.value"
              type={FormFieldType.Radio}
              options={yesNoOptions}
              label="Does anyone in your family have a disability or a nervous or mental disorder?"
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="If yes, please explain. Provide a brief summary of what the disability or disorder is and how it may affect your family's daily life. "
              name="miscLifestyle.mentalDisorder.explanation"
              control={control}
              multiline
              rows={6}
              disabled={watchMentalDisorder !== 'yes'}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              control={control}
              name="miscLifestyle.convicted.value"
              type={FormFieldType.Radio}
              options={yesNoOptions}
              label="Has anyone in your family ever been convicted of a crime? (Please note that criminal background checks will be conducted in accordance with DOS regulations.)"
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="If yes, please explain.  Provide additional information on the member of the host family involved, when it occurred, what were the circumstances and outcome."
              name="miscLifestyle.convicted.explanation"
              control={control}
              multiline
              rows={6}
              disabled={watchConvicted !== 'yes'}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              control={control}
              name="miscLifestyle.contactWithChildServices.value"
              type={FormFieldType.Radio}
              options={yesNoOptions}
              label="Have you had any contact with any child protective services agency in the past? (U.S. Department of State regulatory question)"
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="If yes, please explain. Provide additional information on the member of the host family involved, when it occurred, what were the circumstances and outcome."
              name="miscLifestyle.contactWithChildServices.explanation"
              control={control}
              multiline
              rows={6}
              disabled={watchContactWithChildServices !== 'yes'}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
        </Grid2>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Financial Resources</Typography>
          </Grid2>
          <Grid2 size={12}>
            <Alert variant="outlined" severity="info">
              The income data collected will be used solely for the purposes of determining that the basic needs of the exchange student can be met, including
              three quality meals and transportation to and from school activities.
            </Alert>
          </Grid2>
          <Grid2 size={6}>
            <FormField
              type={FormFieldType.Dropdown}
              label="Please indicate your income range:"
              name="financial.incomeRange"
              control={control}
              options={incomeRangeOptions}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="Can you provide additional information regarding your financial status and ability to suppport another member in your household?"
              name="financial.financialStatus"
              control={control}
              multiline
              rows={6}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              control={control}
              name="financial.publicAssistance.value"
              type={FormFieldType.Radio}
              options={yesNoOptions}
              label="Does anyone in the home receive any public assistance?"
            />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              type={FormFieldType.Text}
              label="If yes, please indicate when and for what reason(s)."
              name="financial.publicAssistance.explanation"
              control={control}
              multiline
              rows={6}
              disabled={watchPublicAssistance !== 'yes'}
              maxLength={MAX_CHARACTERS_DESCRIPTION}
            />
          </Grid2>
        </Grid2>
      </Paper>
    </Stack>
  );
};
